// Security utilities and configurations for n8n2py
// Implements security headers, rate limiting, and authentication validation

export interface SecurityConfig {
  rateLimit: {
    maxRequests: number;
    windowMs: number;
  };
  cors: {
    allowedOrigins: string[];
    allowedMethods: string[];
  };
  csp: {
    enabled: boolean;
    directives: Record<string, string[]>;
  };
}

// Enhanced security configuration with stricter settings
export const defaultSecurityConfig: SecurityConfig = {
  rateLimit: {
    maxRequests: 60, // Reduced for stricter control
    windowMs: 15 * 60 * 1000, // 15 minutes
  },
  cors: {
    allowedOrigins: [
      'https://n8n2py.me',
      'https://www.n8n2py.me',
      ...(typeof import.meta !== 'undefined' && import.meta.env?.DEV
        ? ['http://localhost:3000', 'http://localhost:5173']
        : []),
    ],
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
    allowedHeaders: ['Content-Type', 'Authorization', 'X-Requested-With'],
    credentials: true,
  },
  csp: {
    enabled: true,
    directives: {
      'default-src': ["'self'"],
      'script-src': ["'self'"], // Removed unsafe-inline for production security
      'style-src': ["'self'", "'unsafe-inline'"], // Keep unsafe-inline for styles (required for some components)
      'img-src': ["'self'", 'data:', 'https:', 'blob:'],
      'connect-src': [
        "'self'",
        'https://*.supabase.co',
        'wss://*.supabase.co',
        'https://api.stripe.com',
        'https://js.stripe.com',
        'https://*.google-analytics.com',
        'https://analytics.google.com',
        'https://stats.g.doubleclick.net',
        'https://*.doubleclick.net'
      ],
      'frame-src': ["'none'"],
      'object-src': ["'none'"],
      'base-uri': ["'self'"],
      'form-action': ["'self'"],
      'frame-ancestors': ["'none'"],
      'upgrade-insecure-requests': [],
    },
  },
};

// Enhanced rate limiting implementation with distributed storage simulation
class RateLimiter {
  private requests: Map<string, { count: number; resetTime: number; violations: number }> = new Map();
  private readonly maxViolations = 5;
  private readonly violationWindow = 60 * 60 * 1000; // 1 hour

  isAllowed(identifier: string, config: SecurityConfig['rateLimit']): boolean {
    const now = Date.now();
    const existing = this.requests.get(identifier);

    if (!existing || now > existing.resetTime) {
      // Reset or create new window
      this.requests.set(identifier, {
        count: 1,
        resetTime: now + config.windowMs,
        violations: 0,
      });
      return true;
    }

    if (existing.count >= config.maxRequests) {
      // Track violations for potential blocking
      existing.violations++;

      // Log security event for rate limit violation
      logSecurityEvent('rate_limit_exceeded', {
        identifier,
        count: existing.count,
        maxRequests: config.maxRequests,
        violations: existing.violations,
      });

      // Block IP after max violations
      if (existing.violations >= this.maxViolations) {
        logSecurityEvent('rate_limit_blocked', {
          identifier,
          violations: existing.violations,
          blockDuration: this.violationWindow,
        });
        return false;
      }

      return false;
    }

    existing.count++;
    return true;
  }

  getRemainingTime(identifier: string): number {
    const existing = this.requests.get(identifier);
    if (!existing) return 0;
    return Math.max(0, existing.resetTime - Date.now());
  }

  isBlocked(identifier: string): boolean {
    const existing = this.requests.get(identifier);
    if (!existing) return false;

    const now = Date.now();
    if (existing.violations >= this.maxViolations && now < existing.resetTime + this.violationWindow) {
      return true;
    }

    // Reset violations if window has passed
    if (now > existing.resetTime + this.violationWindow) {
      existing.violations = 0;
    }

    return false;
  }

  getViolations(identifier: string): number {
    const existing = this.requests.get(identifier);
    return existing?.violations || 0;
  }
}

// Global rate limiter instance
export const rateLimiter = new RateLimiter();

// Enhanced security headers utility
export function getSecurityHeaders(config: SecurityConfig = defaultSecurityConfig): Record<string, string> {
  const headers: Record<string, string> = {};

  if (config.csp.enabled) {
    const cspDirectives = Object.entries(config.csp.directives)
      .filter(([_, values]) => values.length > 0)
      .map(([key, values]) => `${key} ${values.join(' ')}`)
      .join('; ');
    headers['Content-Security-Policy'] = cspDirectives;
  }

  // Essential security headers
  headers['X-Content-Type-Options'] = 'nosniff';
  headers['X-Frame-Options'] = 'DENY';
  headers['X-XSS-Protection'] = '1; mode=block';
  headers['Referrer-Policy'] = 'strict-origin-when-cross-origin';
  headers['Cross-Origin-Embedder-Policy'] = 'require-corp';
  headers['Cross-Origin-Opener-Policy'] = 'same-origin';
  headers['Cross-Origin-Resource-Policy'] = 'same-origin';

  // Enhanced permissions policy
  headers['Permissions-Policy'] = [
    'camera=()',
    'microphone=()',
    'geolocation=()',
    'interest-cohort=()',
    'browsing-topics=()',
    'usb=()',
    'payment=()',
    'bluetooth=()',
    'magnetometer=()',
    'gyroscope=()',
    'accelerometer=()',
    'ambient-light-sensor=()'
  ].join(', ');

  // Additional security headers
  headers['Expect-CT'] = 'max-age=86400, enforce';
  headers['Strict-Transport-Security'] = 'max-age=31536000; includeSubDomains; preload';

  return headers;
}

// Enhanced input validation and sanitization utilities
export function sanitizeInput(input: string): string {
  if (typeof input !== 'string') {
    throw new Error('Input must be a string');
  }

  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim()
    .slice(0, 1000); // Limit length
}

// Validate email format
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
}

// Validate URL format
export function validateUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
}

// Validate and sanitize file uploads
export function validateFileUpload(file: File, allowedTypes: string[] = [], maxSize: number = 5 * 1024 * 1024): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: `File size exceeds limit of ${maxSize / (1024 * 1024)}MB` };
  }

  // Check file type if restrictions provided
  if (allowedTypes.length > 0) {
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    const isAllowed = allowedTypes.some(type =>
      fileType.includes(type.toLowerCase()) ||
      fileExtension === type.replace('.', '').toLowerCase()
    );

    if (!isAllowed) {
      return { valid: false, error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` };
    }
  }

  return { valid: true };
}

// SQL injection prevention
export function sanitizeSqlInput(input: string): string {
  return input
    .replace(/[';\\]/g, '') // Remove potentially dangerous characters
    .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi, '') // Remove SQL keywords
    .trim()
    .slice(0, 255); // Limit length for database fields
}

// XSS prevention for rich text
export function sanitizeRichText(input: string): string {
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '') // Remove object tags
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '') // Remove embed tags
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .slice(0, 50000); // Limit length
}

// API request wrapper with rate limiting
export async function secureApiRequest<T>(
  url: string,
  options: RequestInit = {},
  identifier: string = 'default'
): Promise<T> {
  // Check rate limit
  if (!rateLimiter.isAllowed(identifier, defaultSecurityConfig.rateLimit)) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }

  // Add security headers to request
  const secureHeaders = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers: secureHeaders,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Secure API request failed:', error);
    throw error;
  }
}

// Authentication validation
export function validateAuthToken(token: string | null): boolean {
  if (!token) return false;

  try {
    // Basic JWT structure validation
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // Check if token is expired (basic check)
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    return payload.exp > now;
  } catch {
    return false;
  }
}

// Environment-based security configuration
export function getEnvironmentSecurityConfig(): SecurityConfig {
  const isProduction = import.meta.env.PROD;
  const isDevelopment = import.meta.env.DEV;

  if (isProduction) {
    return {
      ...defaultSecurityConfig,
      rateLimit: {
        maxRequests: 50, // Stricter in production
        windowMs: 15 * 60 * 1000,
      },
      csp: {
        enabled: true,
        directives: {
          ...defaultSecurityConfig.csp.directives,
          'script-src': ["'self'"], // No unsafe-inline in production
          'style-src': ["'self'"], // No unsafe-inline in production
        },
      },
    };
  }

  if (isDevelopment) {
    return {
      ...defaultSecurityConfig,
      rateLimit: {
        maxRequests: 200, // More lenient in development
        windowMs: 15 * 60 * 1000,
      },
    };
  }

  return defaultSecurityConfig;
}

// Enhanced security monitoring utilities
interface SecurityEvent {
  timestamp: string;
  event: string;
  details: Record<string, any>;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
  ip?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'authentication' | 'authorization' | 'input_validation' | 'rate_limiting' | 'suspicious_activity' | 'error' | 'audit';
}

class SecurityMonitor {
  private events: SecurityEvent[] = [];
  private readonly maxEvents = 1000;
  private readonly flushInterval = 30000; // 30 seconds

  constructor() {
    if (typeof window !== 'undefined') {
      setInterval(() => this.flushEvents(), this.flushInterval);
    }
  }

  logSecurityEvent(
    event: string,
    details: Record<string, any> = {},
    severity: SecurityEvent['severity'] = 'medium',
    category: SecurityEvent['category'] = 'audit'
  ) {
    const securityEvent: SecurityEvent = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href,
      severity,
      category,
    };

    // Add to local storage for persistence
    this.events.push(securityEvent);
    if (this.events.length > this.maxEvents) {
      this.events.shift(); // Remove oldest event
    }

    // Store in sessionStorage for current session
    this.persistEvents();

    // Console logging based on environment and severity
    if (import.meta.env.PROD) {
      if (severity === 'high' || severity === 'critical') {
        console.error('🚨 Security Event:', securityEvent);
      } else {
        console.warn('Security Event:', securityEvent);
      }
    } else {
      // In dev, security events are persisted to sessionStorage — no console noise
    }

    // Send critical events immediately to external service
    if (severity === 'critical' && import.meta.env.PROD) {
      this.sendToExternalService(securityEvent);
    }
  }

  private persistEvents() {
    try {
      sessionStorage.setItem('n8n2py_security_events', JSON.stringify(this.events));
    } catch (error) {
      console.warn('Failed to persist security events:', error);
    }
  }

  private flushEvents() {
    if (this.events.length === 0) return;

    // Send events to external monitoring service in production
    if (import.meta.env.PROD) {
      this.sendToExternalService(this.events);
    }

    // Clear events after sending
    this.events = [];
    sessionStorage.removeItem('n8n2py_security_events');
  }

  private async sendToExternalService(events: SecurityEvent | SecurityEvent[]) {
    const eventsArray = Array.isArray(events) ? events : [events];

    try {
      // Filter out low severity events for external service
      const criticalEvents = eventsArray.filter(event => event.severity === 'critical');

      if (criticalEvents.length > 0) {
        await fetch('/api/security/events', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ events: criticalEvents }),
        });
      }
    } catch (error) {
      console.error('Failed to send security events to external service:', error);
    }
  }

  getEvents(): SecurityEvent[] {
    return [...this.events];
  }

  getEventsByCategory(category: SecurityEvent['category']): SecurityEvent[] {
    return this.events.filter(event => event.category === category);
  }

  getEventsBySeverity(severity: SecurityEvent['severity']): SecurityEvent[] {
    return this.events.filter(event => event.severity === severity);
  }
}

// Global security monitor instance
export const securityMonitor = new SecurityMonitor();

// Enhanced security event logging function
export function logSecurityEvent(
  event: string,
  details: Record<string, any> = {},
  severity: SecurityEvent['severity'] = 'medium',
  category: SecurityEvent['category'] = 'audit'
) {
  securityMonitor.logSecurityEvent(event, details, severity, category);
}

// Export security configuration for use in components
export const securityConfig = getEnvironmentSecurityConfig();